import { useTranslation } from 'react-i18next';
import { Table } from 'react-bootstrap';
import { Tooltip } from 'antd';
import { RptOpportunityDto } from '@api/client';
import dayjs from 'dayjs';

interface Props {
  data: RptOpportunityDto[];
  onCreateActivity: (opp: RptOpportunityDto) => void;
  onViewActivities: (opp: RptOpportunityDto) => void;
}

const STATO_COLORS: Record<string, { bg: string; text: string }> = {
  CLOSED_LOST: { bg: '#ef4444', text: '#fff' },
};

const StatoBadge: React.FC<{ stato?: string }> = ({ stato }) => {
  if (!stato) return <span>—</span>;
  const color = STATO_COLORS[stato.toUpperCase()] ?? { bg: '#BFBFBF', text: '#fff' };
  return (
    <span
      style={{
        backgroundColor: color.bg,
        color: color.text,
        borderRadius: '12px',
        padding: '2px 10px',
        fontSize: '0.78rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {stato.replace(/_/g, ' ')}
    </span>
  );
};

const fmt = (n?: number) =>
  n != null ? n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—';

const fmtDate = (d?: Date) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

const OpportunityTable: React.FC<Props> = ({ data, onCreateActivity, onViewActivities }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '4rem' }}>
        {t('Nessun dato trovato')}
      </div>
    );
  }

  return (
    <div className="table-responsive table-bordered">
      <Table className="mt-0 mb-0">
        <thead className="bg-red">
          <tr>
            <th>{t('Opportunity')}</th>
            <th>{t('Cliente')}</th>
            <th>{t('Stato')}</th>
            <th className="text-end">{t('Importo')}</th>
            <th className="text-end">{t('Importo/anno')}</th>
            <th className="text-end">{t('Probabilità')}</th>
            <th>{t('Data avvio')}</th>
            <th>{t('Data ultima modifica')}</th>
            <th className="text-center">{t('#Attività')}</th>
            <th>{t('Azioni')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              <td>
                {row.linkOppSF ? (
                  <a href={row.linkOppSF} target="_blank" rel="noreferrer" className="fw-semibold text-blue-links">
                    {row.denominazioneOpportunity || row.pkOpportunity || '—'}
                  </a>
                ) : (
                  <span className="fw-semibold">{row.denominazioneOpportunity || row.pkOpportunity || '—'}</span>
                )}
              </td>
              <td>{row.denominazioneCliente || row.fkCliente || '—'}</td>
              <td><StatoBadge stato={row.statoOpportunity} /></td>
              <td className="text-end">{fmt(row.oppImporto)}</td>
              <td className="text-end">{fmt(row.oppImportoAnno)}</td>
              <td className="text-end">
                {row.oppPercProb != null ? `${row.oppPercProb}%` : '—'}
              </td>
              <td>{fmtDate(row.dataPotenzialeAvvio)}</td>
              <td>{fmtDate(row.dataUltimaModifica)}</td>
              <td className="text-center">
                {(() => {
                  const n = row.oppNumAttivitaInAgenda ?? 0;
                  return (
                    <span
                      style={{
                        backgroundColor: n > 0 ? '#304BA2' : '#BFBFBF',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '2px 10px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        minWidth: '28px',
                        display: 'inline-block',
                      }}
                    >
                      {n}
                    </span>
                  );
                })()}
              </td>
              <td className="text-center">
                <Tooltip title={t('Crea attività')}>
                  <i
                    className="fas fa-plus text-blue-links me-3"
                    role="button"
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    onClick={() => onCreateActivity(row)}
                  />
                </Tooltip>
                <Tooltip title={t('Vedi attività collegate')}>
                  <i
                    className="fas fa-list text-blue-links"
                    role="button"
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    onClick={() => onViewActivities(row)}
                  />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default OpportunityTable;
